import {
  createFileRoute,
  Outlet,
  redirect,
  useChildMatches,
  useNavigate,
} from '@tanstack/react-router'
import { SERIAL_STATUSES, type SerialFilters } from '@/features/inventory-serials/api'
import { SerialsScreen } from '@/features/inventory-serials/serials-screen'
import { hasGrant } from '@/lib/me'

const text = (value: unknown) => (typeof value === 'string' && value ? value : undefined)
const positive = (value: unknown) => {
  const number =
    typeof value === 'number' ? value : typeof value === 'string' ? Number(value) : Number.NaN
  return Number.isInteger(number) && number > 0 ? number : undefined
}

/**
 * Route shell for the serial number register (WP11). `serials.$unitId.tsx` is
 * a CHILD of this route in flat file routing, so it renders through the
 * Outlet below. The grant guard is presentation only.
 */
export const Route = createFileRoute('/_authenticated/inventory/serials')({
  validateSearch: (search: Record<string, unknown>): SerialFilters => ({
    status: SERIAL_STATUSES.find((status) => status === search.status),
    q: text(search.q),
    page: positive(search.page),
    perPage: positive(search.perPage),
  }),
  beforeLoad: ({ context }) => {
    if (!hasGrant(context.queryClient, 'stock.view')) throw redirect({ to: '/' })
  },
  component: InventorySerialsRoute,
})

function InventorySerialsRoute() {
  const navigate = useNavigate({ from: Route.fullPath })
  const children = useChildMatches()
  const params = Route.useSearch()

  if (children.length > 0) {
    return <Outlet />
  }

  return (
    <SerialsScreen params={params} onParamsChange={(next) => void navigate({ search: next })} />
  )
}
